import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Lock, User } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === 'admin') navigate('/admin', { replace: true });
    else if (user && user.role) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError('Enter username and password'); return; }
    setError(''); setLoading(true);
    try {
      const data = await login(username, password);
      if (!data.tc_accepted) {
        navigate('/terms', { replace: true });
      } else {
        navigate(data.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Invalid username or password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-between px-6 py-12">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <img src="/logo.png" alt="Payal Garg" className="h-16 sm:h-20 w-auto brightness-0 invert opacity-90 mb-12" />

        <div className="w-full">
          <h1 className="text-[28px] sm:text-[32px] font-bold text-white leading-tight mb-2">Welcome</h1>
          <p className="text-[#8E8E93] text-sm mb-8">Sign in to continue</p>

          {error && (
            <div data-testid="login-error" className="mb-5 p-3 bg-red-500/10 text-red-400 text-sm rounded-2xl border border-red-500/15">{error}</div>
          )}

          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#48484A]" strokeWidth={1.5} />
              <Input
                data-testid="login-username-input"
                type="text"
                className="h-14 pl-12 pr-4 bg-[#1C1C1E] border-0 text-white text-base placeholder:text-[#48484A] focus:ring-1 focus:ring-[#48484A] rounded-2xl"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </div>
            <div className="relative mb-8">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#48484A]" strokeWidth={1.5} />
              <Input
                data-testid="login-password-input"
                type="password"
                className="h-14 pl-12 pr-4 bg-[#1C1C1E] border-0 text-white text-base placeholder:text-[#48484A] focus:ring-1 focus:ring-[#48484A] rounded-2xl"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button data-testid="login-submit-btn" type="submit" className="w-full h-14 bg-[#2C2C2E] text-white hover:bg-[#3A3A3C] rounded-full text-base font-semibold transition-all" disabled={loading}>
              {loading ? 'Signing in...' : 'Login'}
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Button>
          </form>
        </div>
      </div>
      <p className="text-[11px] text-[#48484A] mt-8">Payal Garg - The Designer Studio</p>
    </div>
  );
}
